import { NIP_STEPS, PIN_LENGTH } from './changeNip.constants';
import { useChangeNipForm } from './useChangeNipForm';
import { Modal, OTPInput } from '@chikara/ui';
import styles from './ChangeNipModal.module.css';
import { t } from '@/i18n';

interface ChangeNipModalProps {
    isOpen: boolean;
    onClose: () => void;
    cardUuid: string;
    accountUuid: string;
}

export const ChangeNipModal = ({
    isOpen,
    onClose,
    cardUuid,
    accountUuid,
}: ChangeNipModalProps) => {
    const {
        step,
        localError,
        isLoading,
        isActionDisabled,
        handleNext,
        handlePinChange,
        handlePinConfirmationChange,
    } = useChangeNipForm({ isOpen, onClose, cardUuid, accountUuid });

    const renderContent = () => {
        switch (step) {
            case NIP_STEPS.INITIAL:
                return (
                    <ol className={styles.nipInstructionsList}>
                        <li>{t('dashboard.card.detail.modals.changeNip.initial.step1')}</li>
                        <li>{t('dashboard.card.detail.modals.changeNip.initial.step2')}</li>
                        <li>{t('dashboard.card.detail.modals.changeNip.initial.step3')}</li>
                        <li>{t('dashboard.card.detail.modals.changeNip.initial.step4')}</li>
                    </ol>
                );
            case NIP_STEPS.CREATE:
                return (
                    <div className={styles.otpContainer}>
                        <p>{t('dashboard.card.detail.modals.changeNip.step1.description')}</p>
                        <OTPInput key="step-create" length={PIN_LENGTH} onComplete={handlePinChange} />
                        {localError && <span className={styles.errorFeedback}>{localError}</span>}
                    </div>
                );
            case NIP_STEPS.CONFIRM:
                return (
                    <div className={styles.otpContainer}>
                        <p>{t('dashboard.card.detail.modals.changeNip.step2.description')}</p>
                        <OTPInput key="step-confirm" length={PIN_LENGTH} onComplete={handlePinConfirmationChange} />
                        {localError && <span className={styles.errorFeedback}>{localError}</span>}
                    </div>
                );
            case NIP_STEPS.SUCCESS:
                return (
                    <div className={styles.successContent}>
                        <p className={styles.successDescription}>{t('dashboard.card.detail.modals.changeNip.step3.description')}</p>
                        <p>{t('dashboard.card.detail.modals.changeNip.step3.bullet1')}</p>
                        <p>{t('dashboard.card.detail.modals.changeNip.step3.bullet2')}</p>
                    </div>
                );
            default:
                return null;
        }
    };

    const getTitle = () => {
        const titles: Record<number, string> = {
            [NIP_STEPS.INITIAL]: t('dashboard.card.detail.modals.changeNip.title'),
            [NIP_STEPS.CREATE]: t('dashboard.card.detail.modals.changeNip.step1.title'),
            [NIP_STEPS.CONFIRM]: t('dashboard.card.detail.modals.changeNip.step2.title'),
            [NIP_STEPS.SUCCESS]: t('dashboard.card.detail.modals.changeNip.step3.title'),
        };
        return titles[step] || '';
    };

    const getActionLabel = () => {
        const labels: Record<number, string> = {
            [NIP_STEPS.INITIAL]: t('dashboard.card.detail.modals.changeNip.initial.next'),
            [NIP_STEPS.CREATE]: t('dashboard.card.detail.modals.changeNip.step1.next'),
            [NIP_STEPS.CONFIRM]: t('dashboard.card.detail.modals.changeNip.step2.next'),
            [NIP_STEPS.SUCCESS]: t('dashboard.card.detail.modals.changeNip.step3.finish'),
        };
        return labels[step] || '';
    };


    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={getTitle()}
            actionLabel={getActionLabel()}
            onAction={handleNext}
            isActionLoading={isLoading}
            isActionDisabled={isActionDisabled}
            secondaryActionLabel={step === NIP_STEPS.INITIAL ? t('dashboard.card.detail.modals.changeNip.initial.cancel') || '' : undefined}
            onSecondaryAction={onClose}
            closeOnOverlayClick={step !== NIP_STEPS.SUCCESS}
            closeOnEsc={step !== NIP_STEPS.SUCCESS}
            showCloseButton={step !== NIP_STEPS.SUCCESS}
        >
            <div className={styles.wizardContent}>
                {renderContent()}
            </div>
        </Modal>
    );
};
